import React, { useEffect } from 'react';
import {
  Text, Box, HStack, VStack, Pressable, Icon,
} from '@gluestack-ui/themed';
import { useFormContext, useFieldArray, Control } from 'react-hook-form';
import { Trash2, PlusCircle } from 'lucide-react-native';
import FormField from '@/common/components/forms/FormField';
import ContentGroup from '@/common/components/ContentGroup';
import GenerateWithAIBadge from '@/common/components/GenerateWithAIBadge';
import { useGenerateTaskProperty } from '@/common/hooks/lecturer/useGenerateTaskProperty';
import { useUserFeatures } from '@/common/hooks/useUserFeatures';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import { useDialog } from '@/common/context/DialogContext';
import log from '@/common/utils/logger';
import { parseLLmResponse } from './utils/parseLLmResponse';

interface TaskObjectivesEditorProps {
  isReadOnly?: boolean;
  moduleId?: string;
  version?: string;
  taskId?: string | null;
}

interface DemandFieldsProps {
  dimension: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  isReadOnly: boolean;
}

const DemandFields: React.FC<DemandFieldsProps> = ({ dimension, control, isReadOnly }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `analysis.${dimension}`,
  });

  return (
    <VStack>
      {fields.map((field, fieldIndex) => (
        <HStack key={field.id} space="sm" alignItems="center">
          <Box flex={1}>
            <FormField
              type="text"
              fieldSize="xs"
              readonly={isReadOnly}
              name={`analysis.${dimension}.${fieldIndex}`}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              defaultValue={(field as any).value}
            />
          </Box>
          {!isReadOnly && (
            <Pressable onPress={() => remove(fieldIndex)} $hover={{ opacity: 0.7 }}>
              <Icon as={Trash2} size="sm" color="$textSubtle" />
            </Pressable>
          )}
        </HStack>
      ))}
      {!isReadOnly && (
        <Pressable mt="$2" onPress={() => append('')} $hover={{ opacity: 0.7 }}>
          <Icon as={PlusCircle} size="sm" color="$textSubtle" />
        </Pressable>
      )}
    </VStack>
  );
};

const TaskObjectivesEditor: React.FC<TaskObjectivesEditorProps> = ({
  isReadOnly = true,
  moduleId,
  version,
  taskId: selectedTaskId,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const { evalDimensions, editableDimensionKeys } = useTenantGradingSettings();
  const { showToastMessage } = useDialog();
  const { isFeatureActive } = useUserFeatures();
  const taskAiEnabled = isFeatureActive('lecturer-task-ai');

  // Create draft task data from form values
  const createDraftTaskData = () => ({
    title: getValues('title') || '',
    description: getValues('description') || '',
  });

  // Handle message from the stream
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleMessage = (message: any) => {
    const parsedData = parseLLmResponse(message);
    const dimensions = parsedData?.dimensions || {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.entries(dimensions).forEach(([dim, dimData]: [string, any]) => {
      const dimensionKey = dim.toLowerCase();
      if (!editableDimensionKeys.includes(dimensionKey)) return;
      const demands = Array.isArray(dimData) ? dimData : dimData?.demands || [];
      const validDemands = demands.filter((d: string) => typeof d === 'string' && d.trim() !== '');
      if (validDemands.length > 0) {
        setValue(`analysis.${dimensionKey}`, validDemands, { shouldValidate: true, shouldDirty: true });
      }
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSuccess = (finalMessage: { valid: boolean; data: any }) => {
    if (finalMessage.valid) {
      showToastMessage('success', 'Generated objectives successfully');
      handleMessage(finalMessage.data);
    } else {
      showToastMessage('error', 'Failed to generate objectives');
    }
  };

  const handleError = (error: Error | string) => {
    showToastMessage('error', 'Failed to generate objectives');
    log.info('[AI] Error generating objectives:', error);
  };

  const { startStream, isStreaming, stopStream } = useGenerateTaskProperty({
    property: 'objectives',
    moduleId: moduleId || '',
    version: version || '',
    taskId: selectedTaskId || '',
    onMessage: handleMessage,
    onServerClose: handleSuccess,
    onError: handleError,
  });

  useEffect(() => () => stopStream(), [stopStream]);

  const handleGenerateObjectives = () => {
    editableDimensionKeys.forEach((dim) => {
      setValue(`analysis.${dim}`, [], { shouldValidate: true });
    });
    startStream(null, createDraftTaskData());
  };

  return (
    <Box>
      {/* Header with section title and AI button */}
      <HStack alignItems="center" justifyContent="space-between" mb="$1" ml="$1">
        <Text size="xs" fontWeight="bold" color="$textSubtle" textTransform="uppercase" letterSpacing="$md">
          Task Objectives
        </Text>

        {!isReadOnly && taskAiEnabled && !!selectedTaskId && (
          <GenerateWithAIBadge
            testID="objectives-generate-badge"
            onGenerate={handleGenerateObjectives}
            isStreaming={isStreaming}
          />
        )}
      </HStack>

      {/* Content group */}
      <ContentGroup variant="accent" p="$4">
        <VStack space="lg">
          {editableDimensionKeys
            .map((dim) => {
              const dimensionLabel = evalDimensions[dim];
              const objectives = getValues(`analysis.${dim}`);
              const hasObjectives = Array.isArray(objectives) && objectives.length > 0;

              // In read-only mode, only show dimensions that have objectives
              if (isReadOnly && !hasObjectives) return null;

              return (
                <VStack key={dim} space="xs">
                  <Text size="2xs" color="$textSubtle" fontWeight="bold" textTransform="uppercase">
                    {dim}
                    {' '}
                    -
                    {' '}
                    {dimensionLabel}
                  </Text>
                  <DemandFields dimension={dim} control={control} isReadOnly={isReadOnly} />
                </VStack>
              );
            })}
          {isReadOnly && editableDimensionKeys
            .every((dim) => {
              const objectives = getValues(`analysis.${dim}`);
              return !Array.isArray(objectives) || objectives.length === 0;
            }) && (
            <Text size="xs" color="$textSubtle" italic>
              No objectives defined for this task.
            </Text>
          )}
        </VStack>
      </ContentGroup>
    </Box>
  );
};

export default TaskObjectivesEditor;
